/**
 * @fileoverview Service for REST API calls
 * @custom:security This service handles API interactions. Always validate responses and handle errors gracefully.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Service class for REST API interactions
 */
export class ApiService {
    constructor() {
        this.baseURL = API_BASE_URL;
        this.token = localStorage.getItem('auth_token');
    }

    /**
     * Sets the authentication token
     * @param {string} token - JWT token
     */
    setToken(token) {
        this.token = token;
        if (token) {
            localStorage.setItem('auth_token', token);
        } else {
            localStorage.removeItem('auth_token');
        }
    }

    /**
     * Gets the authentication token
     * @returns {string|null} JWT token
     */
    getToken() {
        return localStorage.getItem('auth_token');
    }

    /**
     * Makes an API request
     * @private
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Fetch options
     * @returns {Promise<Object>} Response data
     */
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const token = this.getToken();

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers,
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
            });

            let data;
            const responseText = await response.text();

            try {
                data = JSON.parse(responseText);
            } catch (e) {
                // If parsing fails, use the text as the error message or data
                data = { message: responseText || `HTTP Error ${response.status}` };
            }

            if (!response.ok) {
                const error = new Error(data.message || `HTTP error! status: ${response.status}`);
                // Attach additional data from response (e.g. twoFactorRequired)
                Object.assign(error, data);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('API request failed:', error);
            throw error;
        }
    }

    /**
     * HTTP GET request
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Fetch options
     * @returns {Promise<Object>} Response with data property
     */
    async get(endpoint, options = {}) {
        const data = await this.request(endpoint, { ...options, method: 'GET' });
        return { data };
    }

    /**
     * HTTP POST request
     * @param {string} endpoint - API endpoint
     * @param {Object} body - Request body
     * @param {Object} options - Fetch options
     * @returns {Promise<Object>} Response with data property
     */
    async post(endpoint, body, options = {}) {
        const data = await this.request(endpoint, {
            ...options,
            method: 'POST',
            body: JSON.stringify(body),
        });
        return { data };
    }

    /**
     * HTTP PUT request
     * @param {string} endpoint - API endpoint
     * @param {Object} body - Request body
     * @param {Object} options - Fetch options
     * @returns {Promise<Object>} Response with data property
     */
    async put(endpoint, body, options = {}) {
        const data = await this.request(endpoint, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(body),
        });
        return { data };
    }

    /**
     * HTTP DELETE request
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Fetch options
     * @returns {Promise<Object>} Response with data property
     */
    async delete(endpoint, options = {}) {
        const data = await this.request(endpoint, { ...options, method: 'DELETE' });
        return { data };
    }

    /**
     * Download file (Blob)
     * @param {string} endpoint - API endpoint
     * @param {Object} options - Fetch options
     * @returns {Promise<Blob>} File blob
     */
    async download(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`;
        const token = this.getToken();

        const headers = {
            ...options.headers,
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const response = await fetch(url, {
                ...options,
                headers,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP error! status: ${response.status}`);
            }

            return await response.blob();
        } catch (error) {
            console.error('API download failed:', error);
            throw error;
        }
    }

    /**
     * Login
     * @param {string} username - Username
     * @param {string} password - Password
     * @returns {Promise<Object>} Login result
     */
    async login(username, password) {
        const data = await this.request('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ username, password }),
        });

        if (data.token) {
            this.setToken(data.token);
        }

        return data;
    }

    /**
     * Logout
     * @returns {Promise<void>}
     */
    async logout() {
        try {
            await this.request('/auth/logout', {
                method: 'POST',
            });
        } finally {
            this.setToken(null);
        }
    }

    /**
     * Gets list of certificates
     * @param {Object} filters - Filter options
     * @returns {Promise<Object>} Certificates list
     */
    async getCertificates(filters = {}) {
        const queryParams = new URLSearchParams(filters).toString();
        return this.request(`/certificates?${queryParams}`);
    }

    /**
     * Gets a certificate by ID
     * @param {string} certificateId - Certificate ID
     * @returns {Promise<Object>} Certificate data
     */
    async getCertificate(certificateId) {
        return this.request(`/certificates/${certificateId}`);
    }

    /**
     * Issues a new certificate
     * @param {Object} certificateData - Certificate data
     * @returns {Promise<Object>} Created certificate
     */
    async issueCertificate(certificateData) {
        return this.request('/certificates', {
            method: 'POST',
            body: JSON.stringify(certificateData),
        });
    }

    /**
     * Issues a certificate from PDF
     * @param {FormData} formData - Form data with PDF file
     * @returns {Promise<Object>} Created certificate
     */
    async issueCertificateFromPDF(formData) {
        const token = this.getToken();
        const url = `${this.baseURL}/certificates/pdf`;

        const headers = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: formData,
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || `HTTP error! status: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Verifies a certificate
     * @param {string} certificateId - Certificate ID
     * @returns {Promise<Object>} Verification result
     */
    async verifyCertificate(certificateId) {
        return this.request(`/certificates/${certificateId}/verify`, {
            method: 'POST',
        });
    }

    /**
     * Gets list of students/clients
     * @param {Object} filters - Filter options
     * @returns {Promise<Object>} Students list
     */
    async getStudents(filters = {}) {
        const queryParams = new URLSearchParams(filters).toString();
        return this.request(`/students?${queryParams}`);
    }

    /**
     * Gets certificate history
     * @param {Object} filters - Filter options
     * @returns {Promise<Object>} History data
     */
    async getHistory(filters = {}) {
        const queryParams = new URLSearchParams(filters).toString();
        return this.request(`/history?${queryParams}`);
    }

    /**
     * Get blockchain stats (admin/certifier)
     * @returns {Promise<Object>} Blockchain stats including pause status
     */
    async getBlockchainStats() {
        return this.request('/admin/blockchain/stats');
    }
}

// Singleton instance
export const apiService = new ApiService();
